import { useEffect } from 'react'
import { useSelector } from '@/libs/state'
import type { CompanyWorkflow } from '../domain/workflow'
import { useWorkflows } from '../hooks/use-workflows'
import { useWorkItems } from '../hooks/use-work-items'
import { useWorkItemDetail } from '../hooks/use-work-item-detail'
import { workflows$ } from '../store'
import { resetWorkItemComposers } from '../work-item-detail-store'
import { WorkItemDetailContext } from './work-item/work-item-detail-context'
import { WorkItemHeader } from './work-item/work-item-header'
import { WorkItemMain } from './work-item/work-item-main'
import { WorkItemSidebar } from './work-item/work-item-sidebar'
import { WorkItemDialogs } from './work-item/work-item-dialogs'
import { WorkItemEditSheet } from './work-item-edit-sheet'
import { PageLayoutContainer } from '@/components/page-layout'

type Props = {
	workflowId: string
	workItemId: string
}

const WorkItemPage = ({ workflowId, workItemId }: Props) => {
	useWorkflows()
	useWorkItems(workflowId)
	const workflows = useSelector(workflows$)

	const workflow = workflows.find(w => w.id === workflowId) ?? null
	if (!workflow) return null

	return <WorkItemPageContent workflow={workflow} workItemId={workItemId} />
}

type ContentProps = {
	workflow: CompanyWorkflow
	workItemId: string
}

const WorkItemPageContent = ({ workflow, workItemId }: ContentProps) => {
	const detail = useWorkItemDetail(workflow, workItemId)

	// Composer/dialog UI state lives in a module store shared with the sheet;
	// clear it when switching to a different work item so drafts don't leak.
	useEffect(() => resetWorkItemComposers, [workItemId])

	if (!detail.item) return null

	return (
		<WorkItemDetailContext.Provider value={{ workflow, detail }}>
			<PageLayoutContainer className='gap-6 mx-auto w-full max-w-5xl'>
				<WorkItemHeader />

				<div className='flex flex-col gap-8 lg:flex-row lg:items-start'>
					<WorkItemMain />
					<WorkItemSidebar />
				</div>

				<WorkItemDialogs />
				<WorkItemEditSheet />
			</PageLayoutContainer>
		</WorkItemDetailContext.Provider>
	)
}

export { WorkItemPage }
